/**
 * EDC-19 - Yamato Club early access banner on drop product pages.
 *
 * The banner and the modal are rendered server side by
 * snippets/yamato-club-banner.liquid, so there is nothing to inject and no
 * observer to run. The variant and its selling plan are resolved in Liquid and
 * arrive as data attributes, which removes the /products/yamato-club.js
 * round trip the click used to make.
 *
 * Adding follows the theme's own product form (assets/product.js): POST to
 * routes.cart_add_url with the cart sections, then dispatch
 * `switch:cart:afteradditem` so the Alpine cart drawer re-renders itself.
 */
(() => {
  const ROOT_SELECTOR = '.yamato-club';
  const OPEN_CLASS = 'yamato-club-modal-open';

  const getTheme = () => window.theme || {};

  const isCartPage = () => {
    const cartUrl = (getTheme().routes || {}).cart_url || '/cart';
    return window.location.pathname === cartUrl;
  };

  const cartSections = () => {
    const cartType = (getTheme().settings || {}).cart_type;

    if (cartType === 'drawer' && !isCartPage()) {
      return 'drawer-cart-items,drawer-cart-footer,cart-item-count';
    }

    if (isCartPage()) {
      return 'cart-items,cart-footer,cart-item-count';
    }

    return 'cart-item-count';
  };

  const track = (label) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'GA4event',
      'ga4-event-name': label === 'EDC19_ModalClick' ? 'GA4event' : 'cro_event',
      'ga4-event-p1-name': 'event_category',
      'ga4-event-p1-value': label,
      'ga4-event-p2-name': 'event_label',
      'ga4-event-p2-value': '',
    });
  };

  const requestConfig = () => {
    if (typeof fetchConfigDefaults === 'function') {
      return fetchConfigDefaults('application/javascript');
    }

    return {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json;',
        Accept: 'application/javascript',
      },
    };
  };

  const setup = (root) => {
    if (root.dataset.yamatoClubReady === 'true') return;
    root.dataset.yamatoClubReady = 'true';

    const modal = root.querySelector('.yamato-club-modal');
    const learnButton = root.querySelector('.yamato-club-banner__learn');

    if (!modal || !learnButton) return;

    // The modal is position:fixed, so it has to hang off <body> rather than sit
    // inside the product column, where a transformed ancestor could trap it.
    document.body.appendChild(modal);

    const joinButton = modal.querySelector('.yamato-club-modal__join');
    const noThanksButton = modal.querySelector('.yamato-club-modal__no-thanks');
    const overlay = modal.querySelector('.yamato-club-modal__overlay');
    const joinLabel = joinButton ? joinButton.textContent.trim() : '';

    const openModal = () => {
      track('EDC19_ModalClick');
      modal.classList.add('is-active');
      document.body.classList.add(OPEN_CLASS);

      if (joinButton) joinButton.focus();
    };

    const closeModal = () => {
      modal.classList.remove('is-active');
      document.body.classList.remove(OPEN_CLASS);
      learnButton.focus();
    };

    const addToCart = async () => {
      const variantId = modal.getAttribute('data-variant-id');
      const sellingPlanId = modal.getAttribute('data-selling-plan-id');

      if (!variantId || !joinButton || joinButton.disabled) return;

      joinButton.disabled = true;
      joinButton.textContent = 'ADDING...';

      const config = requestConfig();
      const item = {
        id: variantId,
        quantity: 1,
        sections: cartSections(),
        sections_url: window.location.pathname,
      };

      if (sellingPlanId) {
        item.selling_plan = sellingPlanId;
      }

      config.body = JSON.stringify(item);

      const endpoint = (getTheme().routes || {}).cart_add_url || '/cart/add';

      try {
        const response = await fetch(endpoint, config);
        const data = await response.json();

        if (data && data.status) {
          throw new Error(data.description || data.message || data.status);
        }

        closeModal();

        document.body.dispatchEvent(
          new CustomEvent('switch:cart:afteradditem', {
            bubbles: true,
            detail: { response: data },
          })
        );
      } catch (error) {
        console.error('[yamato-club]', error);
      } finally {
        joinButton.disabled = false;
        joinButton.textContent = joinLabel;
      }
    };

    learnButton.addEventListener('click', openModal);
    if (noThanksButton) noThanksButton.addEventListener('click', closeModal);
    if (overlay) overlay.addEventListener('click', closeModal);
    if (joinButton) joinButton.addEventListener('click', addToCart);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-active')) {
        closeModal();
      }
    });

    track('EDC19_ViewedDropProduct');
  };

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
